import React, { useRef, useState } from "react";
import { ArrowDown, Signal, Radio } from "lucide-react";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/0d225e69-bbdf-46e2-9a5f-5e718f2e6f47/images/6f1c54c3b633d211b930325e555e84840fb05269af4ee52ac27f6c2c8adb4258.png";

// Free Pexels stock cell tower / 5G antenna footage (direct CDN).
// If unavailable in a given region, the <img> Ken-Burns layer remains visible underneath.
const VIDEO_SOURCES = [
  "https://videos.pexels.com/video-files/7222866/7222866-uhd_2560_1440_30fps.mp4",
  "https://videos.pexels.com/video-files/3129957/3129957-hd_1920_1080_30fps.mp4",
];

const Hero = () => {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  const scrollToEarly = () => {
    document.getElementById("early-access")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate flex min-h-[100svh] w-full items-end overflow-hidden bg-black"
    >
      {/* Layer 1: still image with Ken Burns (always visible base) */}
      <img
        src={HERO_IMAGE}
        alt="Cinematic cell tower at sunset"
        className={`kenburns absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
          videoReady ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
      />

      {/* Layer 2: stock video (if it loads) */}
      <video
        ref={videoRef}
        data-testid="hero-video"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
          videoReady ? "opacity-100" : "opacity-0"
        }`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setVideoReady(true)}
        onError={() => setVideoReady(false)}
        poster={HERO_IMAGE}
      >
        {VIDEO_SOURCES.map((src) => (
          <source key={src} src={src} type="video/mp4" />
        ))}
      </video>

      {/* Layer 3: gradients & grain for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(249,115,22,0.18),transparent_55%)]" />
      <div className="grain pointer-events-none absolute inset-0" />

      {/* Layer 4: scanline */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden mix-blend-screen">
        <div className="scanline absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-orange-500/10 to-transparent" />
      </div>

      {/* Top status chip */}
      <div className="absolute left-1/2 top-24 z-10 -translate-x-1/2 md:left-10 md:translate-x-0">
        <div
          data-testid="hero-status-chip"
          className="rise inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-neutral-200 backdrop-blur-xl"
        >
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" />
          </span>
          Live: Tower #1042 · 5G · Online
        </div>
      </div>

      {/* Floating signal pin (top right) */}
      <div className="absolute right-8 top-32 z-10 hidden md:block">
        <div className="rise rise-delay-2 relative flex h-12 w-12 items-center justify-center">
          <div className="pulse-pin absolute inset-0 rounded-full" />
          <div className="relative grid h-12 w-12 place-items-center rounded-full border border-orange-500/40 bg-black/60 backdrop-blur-md">
            <Signal className="h-5 w-5 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 lg:px-10 lg:pb-28">
        <div className="max-w-3xl">
          <p
            data-testid="hero-eyebrow"
            className="rise mb-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-orange-400"
          >
            <Radio className="h-3.5 w-3.5" /> InfraVest Platform · Pre-launch
          </p>
          <h1
            data-testid="hero-headline"
            className="rise rise-delay-1 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl"
          >
            Infrastructure
            <br />
            ownership is{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent">
                evolving.
              </span>
              <span className="absolute -bottom-2 left-0 h-[3px] w-full bg-gradient-to-r from-orange-500 to-yellow-400 opacity-70" />
            </span>
          </h1>
          <p
            data-testid="hero-subcopy"
            className="rise rise-delay-2 mt-8 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
          >
            Own a <span className="text-orange-400 font-medium">fractional share</span> of the
            towers, fiber and edge nodes that power modern life. InfraVest opens
            the door for individuals — and the owners building it — to
            participate.
          </p>

          <div className="rise rise-delay-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-join"
              onClick={scrollToEarly}
              className="group inline-flex h-12 items-center gap-2 rounded-full bg-orange-500 px-7 text-sm font-semibold text-neutral-950 transition-all duration-300 hover:bg-orange-400 hover:shadow-[0_18px_60px_-12px_rgba(249,115,22,0.6)]"
            >
              Join Early Access
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              href="#vision"
              data-testid="hero-cta-explore"
              className="inline-flex h-12 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 text-sm font-medium text-white backdrop-blur-md transition-colors hover:border-white/30 hover:bg-white/10"
            >
              Explore the thesis
            </a>
          </div>

          {/* Quick stats strip */}
          <div className="rise rise-delay-4 mt-14 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8">
            {[
              { v: "$2.1T", l: "Global tower & fiber market" },
              { v: "320k+", l: "Macro towers in the U.S." },
              { v: "2025+", l: "Edge build-out cycle" },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-display text-2xl font-semibold text-white sm:text-3xl">
                  {s.v}
                </div>
                <div className="mt-1 text-xs text-neutral-400">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-neutral-400">
        <div className="flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em]">
          Scroll
          <span className="h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
